<template>
	<BlockVisibilityObserver title="IN_STOCK">
		<section class="kova-lp-product" :class="{ 'kova-lp-product_vials': shot === 'vial' }">
			<div class="kova-lp-product__inner">
				<h2 class="kova-lp-product__title">
					<span
						v-for="(part, index) in titleParts"
						:key="index"
						:class="{ 'kova-lp-product__title_accent': part.accent }"
						v-text="part.text"
					/>
				</h2>

				<div class="kova-lp-product__cards" :class="{ 'kova-lp-product__cards_wide': includedMobileOnly }">
					<div v-for="item in products" :key="item.name" class="kova-lp-product__card">
						<div class="kova-lp-product__spec">
							<p class="kova-lp-product__flag" :class="`kova-lp-product__flag_${item.flag.tone}`">
								<InlineSvg
									class="kova-lp-product__flag-icon"
									:src="item.flag.icon === 'lightning' ? lightningIcon : clockIcon"
								/>
								{{ item.flag.label }}
							</p>
							<p class="kova-lp-product__name">{{ item.name }}</p>
							<p class="kova-lp-product__note">{{ item.note }}</p>
							<p class="kova-lp-product__cadence">
								<InlineSvg class="kova-lp-product__cadence-icon" :src="calendarIcon" />
								{{ item.cadence }}
							</p>
						</div>
						<span class="kova-lp-product__shot"><img alt="" :src="item.image" /></span>
					</div>

					<!-- The universal frame drops this card and shows both medications instead. -->
					<div
						v-if="included"
						class="kova-lp-product__card kova-lp-product__card_included"
						:class="{ 'kova-lp-product__card_mobile-only': includedMobileOnly }"
					>
						<p class="kova-lp-product__included-title">{{ includedTitle }}</p>
						<ul class="kova-lp-product__included">
							<li v-for="entry in included" :key="entry" class="kova-lp-product__included-item">
								<InlineSvg class="kova-lp-product__included-icon" :src="checkIcon" />
								<span>{{ entry }}</span>
							</li>
						</ul>
					</div>
				</div>

				<div class="kova-lp-product__actions">
					<div class="kova-lp-product__promises">
						<span v-for="promise in promises" :key="promise.label" class="kova-lp-product__promise">
							<InlineSvg
								class="kova-lp-product__promise-icon"
								:src="promise.icon === 'cross' ? dsCloseIcon : dsCheckIcon"
							/>
							{{ promise.label }}
						</span>
					</div>
					<button class="kova-lp-product__cta" type="button" @click="onCtaClick">{{ ctaLabel }}</button>
				</div>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'

import calendarIcon from '@/assets/kova/landing/ds-calendar-mark.svg'
import checkIcon from '@/assets/kova/landing/ds-check-round.svg'
import clockIcon from '@/assets/kova/landing/ds-clock-filled.svg'
import dsCheckIcon from '@/assets/icons/ds_check.svg'
import dsCloseIcon from '@/assets/kova/landing/ds-close.svg'
import lightningIcon from '@/assets/kova/landing/ds-lightning.svg'

export default {
	name: 'LandingKovaProduct',

	components: { BlockVisibilityObserver },

	props: {
		ctaLabel: {
			type: String,
			required: true,
		},

		// Omit to drop the "Everything you need" card, as the universal frame does.
		included: {
			type: Array,
			default: null,
		},

		// Mobile keeps the card, desktop drops it and widens the row (USA, 6202:58208).
		includedMobileOnly: {
			type: Boolean,
			default: false,
		},

		includedTitle: {
			type: String,
			default: '',
		},

		// [{ flag: { icon, label, tone }, name, note, cadence, image }] — one card per
		// medication the landing offers.
		products: {
			type: Array,
			required: true,
		},

		promises: {
			type: Array,
			required: true,
		},

		// How the product pictures sit in their tiles: `square` — the 1024² shots, oversized
		// and cropped; `vial` — standing vial cut-outs, as the USA frames draw them.
		shot: {
			type: String,
			default: 'square',
			validator: (value) => ['square', 'vial'].includes(value),
		},

		titleParts: {
			type: Array,
			required: true,
		},
	},

	emits: ['progressLoaderCompleted'],

	setup() {
		return { calendarIcon, checkIcon, clockIcon, dsCheckIcon, dsCloseIcon, lightningIcon }
	},

	methods: {
		onCtaClick() {
			this.$emit('progressLoaderCompleted', 'in_stock_eligible')
		},
	},
}
</script>

<style lang="scss" scoped>
// Mobile (Figma 4843:27079): two stacked cards, product shot beside the copy.
.kova-lp-product {
	font-family: var(--kova-lp-font);
	background: var(--kova-lp-page);
}

.kova-lp-product__inner {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding: var(--size-40) var(--size-16) 0;
}

.kova-lp-product__title {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: 1.12;
	letter-spacing: -0.64px;

	color: var(--kova-lp-text);
	text-align: center;
	text-wrap: balance;
}

.kova-lp-product__title_accent {
	color: var(--kova-lp-highlight);
}

.kova-lp-product__cards {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
}

.kova-lp-product__card {
	box-sizing: border-box;
	display: flex;
	gap: var(--size-12);
	padding: var(--size-16);

	border-radius: var(--size-24);
	background: var(--kova-lp-surface);
	box-shadow: inset 0 0 0 2px var(--kova-lp-hairline); // inside stroke, as in Figma
}

.kova-lp-product__spec {
	display: flex;
	flex-direction: column;
	align-items: start;
	gap: var(--size-8);
	order: 1;
	flex: 1;
	min-width: 0;
}

.kova-lp-product__flag {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	margin: 0;
	padding: var(--size-4) var(--size-8) var(--size-4) var(--size-6);

	font-family: var(--font-family);
	font-size: var(--size-12);
	font-weight: 600;
	line-height: var(--size-16);

	border-radius: var(--size-6);
	white-space: nowrap;
}

// Each medication carries its own claim colour: tirzepatide's speed badge is
// purple, semaglutide's price badge mint.
.kova-lp-product__flag_purple {
	color: var(--kova-lp-flag-alt-ink);
	background: #ffe9f4; // button/secondary/purple — no Growth-DS token
}

.kova-lp-product__flag_mint {
	color: var(--kova-lp-brand);
	background: var(--kova-lp-tint);
}

.kova-lp-product__flag-icon :deep(svg) {
	display: block;
	width: var(--size-16);
	height: var(--size-16);
}

.kova-lp-product__name {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-22);
	letter-spacing: -0.32px;

	color: var(--Text-Primary-100);
}

.kova-lp-product__note {
	margin: calc(-1 * var(--size-4)) 0 0;

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);

	color: var(--Text-Primary-100);
}

.kova-lp-product__cadence {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-14);
	font-weight: 600;
	line-height: var(--size-20);

	color: var(--kova-lp-text-soft);
}

.kova-lp-product__cadence-icon :deep(svg) {
	display: block;
	width: var(--size-16);
	height: var(--size-16);
}

// The vial is deliberately larger than its window and cropped by it. The image is
// taken out of flow so the window follows the spec column's height instead of driving
// it — in flow its 160px set the card height and left ~40px of dead space under the
// shorter text column.
.kova-lp-product__shot {
	position: relative;
	overflow: hidden;
	flex-shrink: 0;
	align-self: stretch;

	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--size-84);

	border-radius: var(--size-16);
	background: #e7e5e3; // background/surface-on-primary

	img {
		position: absolute;
		left: 50%;
		top: 50%;

		width: 160px;
		max-width: none;
		height: 160px;

		object-fit: cover;
		transform: translate(-50%, -50%);
	}
}

// USA (6346:46614 / 6346:46638): a standing vial cut-out instead of the square shot — 72 wide
// and 12 from the top of the 84px tile, its foot cut by the tile's bottom edge.
.kova-lp-product_vials .kova-lp-product__shot img {
	top: var(--size-12);
	width: var(--size-72);
	height: auto;

	transform: translateX(-50%);
}

.kova-lp-product__card_included {
	flex-direction: column;
	gap: var(--size-16);
	padding: var(--size-20) var(--size-16);
}

.kova-lp-product__included-title {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-22);

	color: var(--Text-Primary-100);
}

.kova-lp-product__included {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	margin: 0;
	padding: 0;

	list-style: none;
}

.kova-lp-product__included-item {
	display: flex;
	align-items: start;
	gap: var(--size-8);

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);

	color: var(--Text-Primary-100);
}

// `vue-inline-svg` puts the class on the <svg> root, so the size has to be set on
// the element itself — a `:deep(svg)` descendant rule matches nothing and the icon
// falls back to its intrinsic 24px.
.kova-lp-product__included-icon {
	display: block;
	flex-shrink: 0;
	width: var(--size-20);
	height: var(--size-20);

	color: var(--kova-lp-check);
}

.kova-lp-product__actions {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-20);
}

.kova-lp-product__promises {
	display: flex;
	justify-content: center;
	gap: var(--size-8);
}

.kova-lp-product__promise {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	padding: var(--size-4) var(--size-8);

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);

	border-radius: var(--size-8);
	color: var(--kova-lp-ink);
	box-shadow: inset 0 0 0 1px var(--kova-lp-hairline-soft);
	white-space: nowrap;
}

.kova-lp-product__promise-icon {
	color: var(--Text-Primary-100);
}

.kova-lp-product__promise-icon :deep(svg) {
	display: block;
	width: var(--size-20);
	height: var(--size-20);
}

.kova-lp-product__cta {
	box-sizing: border-box;
	width: 100%;
	height: var(--size-52);
	padding: var(--size-16) var(--size-32);

	font-family: inherit;
	font-size: var(--size-16);
	font-weight: 500;
	line-height: var(--size-20);

	border: none;
	border-radius: var(--size-16);
	color: var(--Fill-White);
	background: var(--kova-lp-brand);
	cursor: pointer;
}

// Desktop (Figma 5026:7324): the two cards sit side by side and the product shot
// drops below its copy instead of beside it.
@media (min-width: 1024px) {
	// 1028px of content between Figma's 206px gutters; the cap reproduces them at
	// >=1440 and the gutter falls back to 40 on a narrower board.
	.kova-lp-product__inner {
		box-sizing: border-box;
		max-width: 1108px;
		margin-inline: auto;
		padding: 112px var(--size-40) 0; // 112 top — 5026:7324, not on the size ladder
		gap: var(--size-40);
	}

	// Figma leaves 64px between the cards and the CTA, not the section's 40 (5026:7324).
	.kova-lp-product__actions {
		margin-top: var(--size-24);
	}

	.kova-lp-product__title {
		font-size: var(--size-56);
		line-height: 1.1;
		letter-spacing: -1.12px;

		text-wrap: initial;
	}

	.kova-lp-product__cards {
		flex-direction: row;
		align-items: stretch;
		gap: var(--size-16);
	}

	// 592 + 16 + 420 is exactly the 1028 content width, so at >=1440 both cards land
	// on their Figma size and below that they give up width in proportion.
	.kova-lp-product__card {
		flex-direction: column;
		flex: 1 1 592px;
		min-width: 0;
		gap: var(--size-12);
		justify-content: end;

		border-radius: var(--size-36);
		box-shadow: inset 0 0 0 1px var(--kova-lp-hairline-soft);
	}

	.kova-lp-product__spec {
		gap: var(--size-16);
		padding: var(--size-16);
	}

	.kova-lp-product__flag {
		font-size: var(--size-14);
		line-height: var(--size-20);
	}

	.kova-lp-product__name {
		font-family: var(--kova-lp-font);
		font-size: var(--size-28);
		font-weight: 500;
		line-height: 1.2;
		letter-spacing: -0.56px;

		color: var(--kova-lp-text);
	}

	.kova-lp-product__note {
		margin-top: -8px;

		font-size: var(--size-16);
		line-height: var(--size-22);
	}

	.kova-lp-product__cadence {
		gap: var(--size-6);

		font-size: var(--size-16);
		font-weight: 500;
	}

	.kova-lp-product__cadence-icon :deep(svg) {
		width: var(--size-20);
		height: var(--size-20);
	}

	.kova-lp-product__shot {
		order: 2;
		width: 100%;
		aspect-ratio: 592 / 272; // the shot window, in the card's own proportions

		border-radius: var(--size-24);
	}

	// The vial is deliberately larger than its window and cropped by it. Sized off
	// the card (448 and -36 of 592) so the crop holds when the card narrows.
	// Back into flow here — the desktop card is a column, so the shot window sizes
	// itself from `aspect-ratio` and does not need the mobile out-of-flow trick.
	.kova-lp-product__shot img {
		position: static;

		width: 75.676%;
		max-width: none;
		height: auto;
		aspect-ratio: 1;
		margin-top: -6.081%;
		align-self: start;

		transform: none;
	}

	// USA (6202:58208): a 560x300 tile with the same cut-out standing in it — 180 of its 560
	// and 24 from its top, the foot cut by the bottom edge. In % so it holds as the card narrows.
	.kova-lp-product_vials .kova-lp-product__shot {
		aspect-ratio: 560 / 300;

		img {
			width: 32.143%;
			aspect-ratio: auto;
			margin-top: 4.286%;

			transform: none;
		}
	}

	// The USA desktop frame (6202:58208) has no "Everything you need" card; its two medications
	// fill a 1200px row instead, wider than the 1028 title column — 86px past it on each side
	// at 1440, never past the 40px page gutter on a narrower board.
	.kova-lp-product__cards_wide {
		margin-inline: calc((100% - min(1200px, 100vw - 2 * var(--size-40))) / 2);
	}

	.kova-lp-product__card.kova-lp-product__card_mobile-only {
		display: none;
	}

	.kova-lp-product__card_included {
		flex: 1 1 420px;
		justify-content: space-between;
		gap: var(--size-16);
		padding: var(--size-32) var(--size-32) var(--size-40);
	}

	.kova-lp-product__included-title {
		font-family: var(--kova-lp-font);
		font-size: var(--size-28);
		font-weight: 500;
		line-height: 1.2;
		letter-spacing: -0.56px;

		color: var(--kova-lp-text);
	}

	.kova-lp-product__included {
		gap: var(--size-16);
	}

	.kova-lp-product__included-item {
		font-size: var(--size-18);
		line-height: var(--size-24);
		letter-spacing: -0.2px;
	}

	// Same reason as the mobile rule: the class sits on the <svg> itself.
	.kova-lp-product__included-icon {
		width: var(--size-24);
		height: var(--size-24);
	}

	// Only the CTA survives on desktop; the promise pills belong to the mobile frame.
	.kova-lp-product__promises {
		display: none;
	}

	.kova-lp-product__cta {
		width: 368px;
		max-width: 100%;
		height: 90px; // 5026:7324 — between --size-88 and --size-96, kept exact
		padding: 0 var(--size-24);

		font-size: var(--size-24);
		line-height: normal;

		border-radius: var(--size-28);
	}
}
</style>
